import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import { MohAgreementHolder } from '@principle-theorem/principle-core';
import {
  canSave$,
  LoaderButtonComponent,
  NgDialogModule,
  NgSharedModule,
  PipesModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  IMohEnrolmentData,
  IMohGenerateEnrolmentFormRequest,
  IPatient,
  IPractice,
  NZ_HEALTH_DISTRICTS,
} from '@principle-theorem/principle-core/interfaces';
import {
  DAY_MONTH_YEAR_FORMAT,
  httpsCallable,
  ISODateType,
  serialise,
  SerialisedData,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { MohEnrolmentPdfDataBuilder } from '../../../moh/pdf-data/moh-enrolment-pdf-data-builder';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface IMohGenerateEnrolmentDialogData {
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
  patientMobileNumber?: string;
}

export type MohGenerateEnrolmentForm = Pick<
  IMohEnrolmentData,
  | 'agreementHoldersName'
  | 'agreementNumber'
  | 'changeOfDentalProvider'
  | 'districtArea'
  | 'newEnrolment'
  | 'nhiNumber'
  | 'patientDayPhoneNumber'
  | 'patientMobile'
  | 'patientOrGuardianName'
  | 'patientSchoolYear'
  | 'payeeNumber'
  | 'practicePostalAddress'
  | 'practicePhysicalAddress'
  | 'patientPostcode'
  | 'patientAddress'
  | 'patientDateOfBirth'
> & {
  patientName: string;
};

@Component({
  selector: 'pr-moh-generate-enrolment-form-dialog',
  imports: [
    NgDialogModule,
    MatCheckboxModule,
    PipesModule,
    LoaderButtonComponent,
    NgSharedModule,
    InformationBoxComponent,
  ],
  templateUrl: './moh-generate-enrolment-form-dialog.component.html',
  styleUrl: './moh-generate-enrolment-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MohGenerateEnrolmentFormDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<IMohGenerateEnrolmentDialogData, string>>(MatDialogRef);
  data = inject<IMohGenerateEnrolmentDialogData>(MAT_DIALOG_DATA);

  showRequiredError = signal(false);
  drawnSignature = signal(false);
  loading = signal(false);
  dateFormat = DAY_MONTH_YEAR_FORMAT;
  districts = NZ_HEALTH_DISTRICTS;
  today = moment();

  form = new TypedFormGroup<MohGenerateEnrolmentForm>({
    patientName: new TypedFormControl<string>('', Validators.required),
    patientDateOfBirth: new TypedFormControl<ISODateType>(
      '',
      Validators.required
    ),
    agreementHoldersName: new TypedFormControl<string>('', Validators.required),
    agreementNumber: new TypedFormControl<string>('', Validators.required),
    changeOfDentalProvider: new TypedFormControl<boolean>(false),
    districtArea: new TypedFormControl<string>('', Validators.required),
    newEnrolment: new TypedFormControl<boolean>(true),
    nhiNumber: new TypedFormControl<string>('', Validators.required),
    patientDayPhoneNumber: new TypedFormControl<string>(''),
    patientMobile: new TypedFormControl<string>(''),
    patientOrGuardianName: new TypedFormControl<string>(
      '',
      Validators.required
    ),
    patientSchoolYear: new TypedFormControl<number>(
      undefined,
      Validators.required
    ),
    payeeNumber: new TypedFormControl<string>('', Validators.required),
    practicePostalAddress: new TypedFormControl<string>(''),
    practicePhysicalAddress: new TypedFormControl<string>(''),
    patientAddress: new TypedFormControl<string>('', Validators.required),
    patientPostcode: new TypedFormControl<number>(
      undefined,
      Validators.required
    ),
  });

  constructor() {
    const agreementHolder = MohAgreementHolder.fromPractice(this.data.practice);

    this.form.patchValue({
      patientName: this.data.patient.name,
      agreementHoldersName: agreementHolder.agreementHolderName,
      agreementNumber: agreementHolder.agreementNumber,
      patientDateOfBirth: this.data.patient.dateOfBirth,
      districtArea: this.data.practice.mohSettings?.districtArea,
      nhiNumber: this.data.patient.nationalHealthIndexNumber,
      patientMobile: this.data.patientMobileNumber,
      patientOrGuardianName: this.data.patient.name,
      payeeNumber: agreementHolder.payeeNumber,
      practicePostalAddress: this.data.practice.address,
      practicePhysicalAddress: this.data.practice.address,
      patientAddress: this.data.patient.address,
      patientPostcode: Number(
        this.data.patient.metadata?.address?.components?.postalCode
      ),
    });

    canSave$(this.form)
      .pipe(takeUntilDestroyed())
      .subscribe((canSave) => {
        const showRequiredError = this.showRequiredError();
        if (showRequiredError && canSave) {
          this.showRequiredError.set(false);
        }
      });
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showRequiredError.set(true);
      return;
    }

    this.loading.set(true);
    const enrolmentData = MohEnrolmentPdfDataBuilder.build(
      this.data.practice,
      this.data.patient,
      this.form.getRawValue()
    );
    const request: IMohGenerateEnrolmentFormRequest = {
      patient: this.data.patient.ref,
      practice: this.data.practice.ref,
      data: enrolmentData,
    };
    const formPath = await snapshot(
      httpsCallable<SerialisedData<IMohGenerateEnrolmentFormRequest>, string>(
        'http-moh-generateEnrolmentForm'
      )(serialise(request))
    );

    this.loading.set(false);
    this._dialogRef.close(formPath);
  }
}
